import { useId } from 'react'
import { avatarParams, avatarSeed } from '../utils/playerAvatar'

const VIEW = 20
const C = VIEW / 2

// Bracket arm extension variants (0..2) — how far each corner mark runs
// past the plate's rounded corner along the straight edge.
const BRACKET_ARM = [0.7, 1.5, 0.2]

// Plate geometry shared by the rect and the corner brackets, so the
// brackets trace the plate outline EXACTLY (v30's freehand corners sat
// misaligned against the plate edge).
const P0 = 1.2
const P1 = VIEW - P0
const PR = 4.8
const PA = P0 + PR // where the corner arc meets the straight edge
const PB = P1 - PR

// The sweep hand: a SHORT arc (~110°) orbiting between gem and plate — one
// moving accent with clear negative space around it (the v30 270° ring
// filled the plate and collided with the brackets at small sizes).
const RING_R = 6.3
const RING_CIRC = 2 * Math.PI * RING_R
const SWEEP = RING_CIRC * (110 / 360)

/**
 * The generative player mark (v31 — refinement pass): a HUD CHIP in ONE hue
 * family. The plate is a dark tint of the player's hue, the detection
 * brackets a light tint of the SAME hue (Kasper: brackets should match the
 * avatar's background and vary per player — v30's plain white read
 * misaligned/unrelated), the glass-gem core carries the saturated hue, and
 * the single moving accent is a SHORT sweep hand in a brighter tint of the
 * SAME hue (v33 — the contrasting companion color read out of place),
 * orbiting at the seeded per-player speed/direction. One color family,
 * one moving accent, real negative space.
 *
 * Deterministic per player (name+country); CSS keyframes
 * (social-avatar-spin) drive the sweep, reduced motion stills it. Pure
 * SVG, transform-only animation, cheap at ~150 rows. Decorative only
 * (aria-hidden by the wrapper).
 */
export function PlayerAvatar({
  name,
  country
}: Readonly<{ name: string | undefined; country: string | undefined }>) {
  const uid = useId()
  const p = avatarParams(avatarSeed(name, country))

  const core = `hsl(${p.hue} ${p.sat}% 55%)`
  const coreLight = `hsl(${p.hue} ${p.sat}% 73%)`
  const coreDeep = `hsl(${p.hue} ${Math.min(90, p.sat + 8)}% 30%)`
  const plate = `hsl(${p.hue} ${Math.round(p.sat * 0.45)}% 13%)`
  const plateEdge = `hsl(${p.hue} ${p.sat}% 42%)`
  const bracket = `hsl(${p.hue} ${Math.min(88, p.sat + 6)}% 76%)`
  // v33: the sweep matches the chip's OWN hue (Kasper: the contrasting
  // companion color read out of place) — brighter + more saturated than the
  // brackets so it still reads as the moving element.
  const sweep = `hsl(${p.hue} 90% 66%)`
  const arm = BRACKET_ARM[p.gapVariant] ?? 3.2

  return (
    <svg
      className="social-player-avatar"
      focusable="false"
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient cx="38%" cy="32%" id={`${uid}-core`} r="72%">
          <stop offset="0" stopColor={coreLight} />
          <stop offset="0.55" stopColor={core} />
          <stop offset="1" stopColor={coreDeep} />
        </radialGradient>
      </defs>

      {/* the plate — a dark tint of the player's own hue, so brackets,
          plate and gem read as one built object */}
      <rect
        fill={plate}
        fillOpacity="0.92"
        height={P1 - P0}
        rx={PR}
        stroke={plateEdge}
        strokeOpacity="0.45"
        strokeWidth="0.7"
        width={P1 - P0}
        x={P0}
        y={P0}
      />

      {/* the SWEEP HAND: one short companion-hue arc orbiting the gem at
          the player's seeded pace. The seeded START angle lives in the
          dash OFFSET (not a transform) — mixing an attribute transform
          with the CSS rotation animation made the browser interpolate
          mismatched matrices and the orbit wobbled off-center (v31 bug).
          The animated group carries ONLY the CSS rotation, dead-centered
          via transform-origin + transform-box in the stylesheet. */}
      <g
        className="social-player-avatar__ring"
        style={{
          animationDuration: `${p.spinSeconds}s`,
          animationDirection: p.spinDirection === -1 ? 'reverse' : 'normal'
        }}
      >
        <circle
          cx={C}
          cy={C}
          fill="none"
          r={RING_R}
          stroke={sweep}
          strokeDasharray={`${SWEEP} ${RING_CIRC - SWEEP}`}
          strokeDashoffset={(p.sheenAngle / 360) * RING_CIRC}
          strokeLinecap="round"
          strokeOpacity="0.9"
          strokeWidth="1.4"
        />
      </g>

      {/* the locked core — glass gem, centered, sized to carry the hue */}
      <circle cx={C} cy={C} fill="rgba(8, 10, 14, 0.85)" r="4.9" />
      <circle cx={C} cy={C} fill={`url(#${uid}-core)`} r="4.6" />
      <circle
        cx={C}
        cy={C}
        fill="none"
        r="4.6"
        stroke="rgba(255, 255, 255, 0.25)"
        strokeWidth="0.55"
      />
      <ellipse cx="8.6" cy="8.5" fill="rgba(255, 255, 255, 0.6)" rx="1.55" ry="1.05" />

      {/* detection-bracket corner marks — light tint of the SAME hue,
          tracing the plate's own corner arcs exactly (shared geometry:
          same radius, same edge lines), with a small seeded arm extension */}
      <g
        fill="none"
        stroke={bracket}
        strokeLinecap="round"
        strokeOpacity="0.95"
        strokeWidth="1.6"
      >
        <path d={`M${P0} ${PA + arm} L${P0} ${PA} A${PR} ${PR} 0 0 1 ${PA} ${P0} L${PA + arm} ${P0}`} />
        <path d={`M${PB - arm} ${P0} L${PB} ${P0} A${PR} ${PR} 0 0 1 ${P1} ${PA} L${P1} ${PA + arm}`} />
        <path d={`M${P1} ${PB - arm} L${P1} ${PB} A${PR} ${PR} 0 0 1 ${PB} ${P1} L${PB - arm} ${P1}`} />
        <path d={`M${PA + arm} ${P1} L${PA} ${P1} A${PR} ${PR} 0 0 1 ${P0} ${PB} L${P0} ${PB - arm}`} /></g>
    </svg>
  )
}
